import { LeftOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { ToolbarProps } from 'react-big-calendar';

import type {
  AvailabilityFormValues,
  AvailabilityInitialValues,
} from '../AvailabilityForm/availabilityForm.types';
import { AvailabilityPopover } from '../AvailabilityPopover/AvailabilityPopover';

import styles from './CalendarToolBar.module.scss';

export const NEW_AVAILABILITY_POPOVER_ID = 'new';

const EMPTY_INITIAL_VALUES: AvailabilityInitialValues = {
  startTime: undefined,
  endTime: undefined,
  date: undefined,
  capacity: 1,
};

type Props = ToolbarProps & {
  onCreate?: (values: AvailabilityFormValues) => void;
  onCreateChange?: (values: AvailabilityFormValues | null) => void;
  createInitialValues?: AvailabilityInitialValues;
  durationMinutes?: number;
  selectActivityName?: string;
  isWithinOpeningHours?: (start: Dayjs) => boolean;
  isDayClosed?: (date: Dayjs) => boolean;
};

export const CalendarToolBar = ({
  label,
  onNavigate,
  date,
  onCreate,
  onCreateChange,
  createInitialValues,
  durationMinutes,
  selectActivityName,
  isWithinOpeningHours,
  isDayClosed,
}: Props) => {
  const formatDateRange = (rangeDate: Date) => {
    const start = dayjs(rangeDate).locale('en-gb').startOf('week');
    const end = dayjs(rangeDate).locale('en-gb').endOf('week');

    return `${start.format('DD')}-${end.format('DD')} ${end.format(
      'MMMM'
    )} ${end.format('YYYY')}`;
  };

  return (
    <div className={styles.calendarToolBar}>
      <div className={styles.left}>
        <div className={styles.navigationButtons}>
          <Button
            onClick={() => onNavigate('PREV')}
            className={styles.navButton}
          >
            <LeftOutlined />
          </Button>
          <Button
            size="small"
            onClick={() => onNavigate('TODAY')}
            className={styles.todayButton}
          >
            Today
          </Button>
          <Button
            onClick={() => onNavigate('NEXT')}
            className={styles.navButton}
          >
            <RightOutlined />
          </Button>
        </div>
        {date && (
          <span className={styles.dateLabel}>{formatDateRange(date)}</span>
        )}
      </div>

      <div className={styles.right}>
        <div className={styles.actionButtons}>
          <AvailabilityPopover
            id={NEW_AVAILABILITY_POPOVER_ID}
            title="New availabilities"
            subtitle={selectActivityName ?? ''}
            placement="bottomRight"
            initialValues={createInitialValues ?? EMPTY_INITIAL_VALUES}
            durationMinutes={durationMinutes}
            onSubmit={(values) => onCreate?.(values)}
            onChange={onCreateChange}
            isWithinOpeningHours={isWithinOpeningHours}
            isDayClosed={isDayClosed}
          >
            <Button icon={<PlusOutlined />}>New availabilities</Button>
          </AvailabilityPopover>
        </div>
      </div>
    </div>
  );
};
